import { useState } from 'react';
import { ApiError, usersApi, type ApiSettings } from '../api';
import { t } from '../i18n';
import type { Session } from '../session';
import { passwordProblems, userErrorKey, type PasswordProblem } from '../users';
import { KkpLogo } from './KkpLogo';
import { Tip } from './PageHead';
import { Dialog, useDialogBusy } from './Dialog';

/**
 * Changing your own password (R8) — `POST /api/me/password` with the current and the new one.
 *
 * Two places use the one form: the dialog behind "Change password" in the masthead, and the
 * full-page screen a user with a temporary password lands on after signing in (the admin
 * service answers every other route with 403 `password_change_required` until they do).
 *
 * The passwords live in this component's state for one submit and are cleared on success.
 */

const PROBLEM_KEY: Record<PasswordProblem, string> = {
  short: 'users.problem.short',
  long: 'users.problem.long',
  username_password: 'users.problem.username_password',
  mismatch: 'users.problem.mismatch',
};

export function passwordErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    const key = userErrorKey(e.code);
    if (key) return t(key);
    if (e.problems?.length) return e.problems.map((p) => `${p.field}: ${p.message}`).join('; ');
  }
  return (e as Error).message;
}

function ChangePasswordForm({
  settings,
  session,
  submitLabel,
  onChanged,
  onCancel,
}: {
  settings: ApiSettings;
  session: Session;
  submitLabel: string;
  onChanged: () => void;
  onCancel?: () => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  useDialogBusy(busy);
  const [error, setError] = useState<string | null>(null);

  const username = session.user.username ?? '';
  const problems = passwordProblems(next, username, confirm);
  // Nothing typed yet is not an error yet.
  const shown = next === '' ? [] : problems.filter((p) => p !== 'mismatch' || confirm !== '');
  const ready = current !== '' && problems.length === 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await usersApi.changeOwnPassword(settings, current, next);
      setCurrent('');
      setNext('');
      setConfirm('');
      onChanged();
    } catch (err) {
      setError(passwordErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="stack" onSubmit={submit} aria-label={submitLabel} data-testid="password-change-form">
      <label className="field">
        {t('users.password.current')}
        <input
          type="password"
          autoComplete="current-password"
          aria-label={t('users.password.current')}
          value={current}
          onChange={(ev) => setCurrent(ev.target.value)}
        />
      </label>
      <label className="field">
        <span>
          {t('users.password.new')} <Tip>{t('users.password.hint')}</Tip>
        </span>
        <input
          type="password"
          autoComplete="new-password"
          aria-label={t('users.password.new')}
          className={shown.some((p) => p !== 'mismatch') ? 'invalid' : ''}
          value={next}
          onChange={(ev) => setNext(ev.target.value)}
        />
      </label>
      <label className="field">
        {t('users.password.confirm')}
        <input
          type="password"
          autoComplete="new-password"
          aria-label={t('users.password.confirm')}
          className={shown.includes('mismatch') ? 'invalid' : ''}
          value={confirm}
          onChange={(ev) => setConfirm(ev.target.value)}
        />
      </label>
      {shown.length > 0 && (
        <div className="why-missing" role="alert" data-testid="password-problems" style={{ margin: 0 }}>
          {shown.map((p) => t(PROBLEM_KEY[p])).join(' ')}
        </div>
      )}
      {error && (
        <div className="banner error" role="alert" data-testid="password-error">
          {error}
        </div>
      )}
      <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel}>
            {t('btn.cancel.label')}
          </button>
        )}
        <button type="submit" className="btn primary" data-testid="password-submit" disabled={!ready || busy}>
          {busy ? t('btn.checking.label') : submitLabel}
        </button>
      </div>
    </form>
  );
}

/** From the masthead: change your password without leaving the console. */
export function ChangePasswordDialog({
  settings,
  session,
  onClose,
  onChanged,
}: {
  settings: ApiSettings;
  session: Session;
  onClose: () => void;
  onChanged: () => void;
}) {
  return (
    <Dialog title={t('users.password.title')} plainBody onClose={onClose}>
      <ChangePasswordForm
        settings={settings}
        session={session}
        submitLabel={t('users.password.action')}
        onChanged={onChanged}
        onCancel={onClose}
      />
    </Dialog>
  );
}

/**
 * After signing in with a temporary password: the console does not mount until the user has
 * chosen their own. Styled as the sign-in page, because it is the second half of signing in.
 */
export function ForcedPasswordChange({
  settings,
  session,
  onChanged,
  onSignOut,
}: {
  settings: ApiSettings;
  session: Session;
  onChanged: () => void;
  onSignOut: () => void;
}) {
  return (
    <div className="login-screen" data-testid="forced-password-change">
      <header className="masthead">
        <KkpLogo />
        <span className="masthead-title">{t('shell.brand.name')}</span>
        <span className="spacer" />
        <button type="button" className="btn small" data-testid="forced-sign-out" onClick={onSignOut}>
          {t('btn.signout.label')}
        </button>
      </header>
      <div className="login-body">
        <div className="login-card">
          <h1>{t('users.forced.title')}</h1>
          <div className="banner warn small" role="status">
            {t('users.forced.lead', { name: session.user.displayName })}
          </div>
          <ChangePasswordForm settings={settings} session={session} submitLabel={t('users.forced.continue')} onChanged={onChanged} />
        </div>
      </div>
    </div>
  );
}
